<template>
    <div
        :class="[
            'w-full',
            overlay
                ? 'absolute inset-x-0 top-0 z-30'
                : 'relative z-30 bg-black text-white'
        ]"
    >
        <div class="container mx-auto px-4 py-4 md:px-6 md:py-6 xl:px-8">
            <header class="flex items-center justify-between gap-4">
                <div class="flex min-w-0 items-center gap-8 xl:gap-10">
                    <Link :href="route('home')" class="shrink-0">
                        <IconLogo class="h-auto w-[120px] md:w-[140px] xl:w-[152px]"/>
                    </Link>

                    <nav class="hidden items-center gap-7 xl:flex">
                        <template v-for="item in menuItems" :key="item.label">
                            <TopBarDropdown
                                v-if="item.children?.length"
                                :item="item"
                            />

                            <Link
                                v-else
                                :href="route(item.route_name, item.route_params || {})"
                                class="text-[15px] text-white/90 transition hover:text-baseColor"
                            >
                                {{ item.label }}
                            </Link>
                        </template>
                    </nav>
                </div>

                <div class="hidden items-center gap-5 xl:flex">
                    <BasePhoneComponent
                        class="text-sm text-white transition hover:text-baseColor"
                        :highlightPrefix="true"
                    />

                    <div
                        @click.prevent="callbackStore.showBaseCallbackPopup({ title: 'Обратный звонок' })"
                        class="flex cursor-pointer items-center gap-2 text-sm text-white/90 transition hover:text-baseColor"
                    >
                        <span class="flex h-7 w-7 items-center justify-center rounded-full border border-white/35">
                            <IconPhone class="h-4 w-4"/>
                        </span>
                        <span>Позвоните мне</span>
                    </div>
                </div>

                <div class="flex items-center gap-3 xl:hidden">
                    <BasePhoneComponent
                        class="whitespace-nowrap text-[13px] text-white"
                        :highlightPrefix="true"
                    />

                    <button
                        v-if="menuItems.length > 0"
                        type="button"
                        class="flex h-10 w-10 flex-col items-center justify-center gap-1.5"
                        @click="openMobileMenu"
                    >
                        <span class="block h-0.5 w-6 rounded bg-white"></span>
                        <span class="block h-0.5 w-6 rounded bg-white"></span>
                        <span class="block h-0.5 w-6 rounded bg-white"></span>
                    </button>
                </div>
            </header>

            <transition name="mobile-menu">
                <div
                    v-if="mobileMenuOpen"
                    class="fixed inset-0 z-[100] bg-black xl:hidden"
                >
                    <div class="relative flex h-full flex-col overflow-hidden">
                        <!-- background -->
                        <div class="absolute inset-0">
                            <div class="absolute inset-0 bg-black"></div>
                            <div
                                class="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,196,0,0.12),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.04),transparent_22%)]"
                            ></div>
                        </div>

                        <!-- top -->
                        <div class="relative z-10 border-b border-white/10 px-4 py-4">
                            <div class="flex items-center justify-between gap-4">
                                <Link :href="route('home')" class="shrink-0" @click="closeMobileMenu">
                                    <IconLogo class="h-auto w-[120px]"/>
                                </Link>

                                <button
                                    type="button"
                                    class="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white transition hover:border-baseColor/30 hover:text-baseColor"
                                    @click="closeMobileMenu"
                                    aria-label="Закрыть меню"
                                >
                        <span class="relative block h-4 w-4">
                            <span
                                class="absolute left-1/2 top-1/2 block h-[1.5px] w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded bg-current"></span>
                            <span
                                class="absolute left-1/2 top-1/2 block h-[1.5px] w-4 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded bg-current"></span>
                        </span>
                                </button>
                            </div>
                        </div>

                        <!-- content -->
                        <div class="relative z-10 flex-1 overflow-y-auto px-4 pb-6 pt-4">
                            <nav class="flex flex-col gap-2">
                                <template v-for="item in mobileMenuItems" :key="item.label">
                                    <div
                                        v-if="item.children?.length"
                                        class="overflow-hidden rounded-2xl border border-white/8 bg-white/[0.03]"
                                    >
                                        <button
                                            type="button"
                                            class="flex w-full items-center justify-between gap-3 px-4 py-4 text-left text-[16px] font-medium text-white/90"
                                            @click="toggleMobileDropdown(item.label)"
                                        >
                                            <span>{{ item.label }}</span>

                                            <IconChevronDown
                                                class="h-4 w-4 transition duration-300"
                                                :class="openedMobileDropdown === item.label ? 'rotate-180 text-baseColor' : ''"
                                            />
                                        </button>

                                        <transition name="collapse">
                                            <div
                                                v-if="openedMobileDropdown === item.label"
                                                class="border-t border-white/6 px-2 pb-2 pt-2"
                                            >
                                                <Link
                                                    v-for="child in item.children"
                                                    :key="child.label"
                                                    :href="route(child.route_name, child.route_params || {})"
                                                    class="block rounded-xl px-3 py-3 transition hover:bg-white/[0.04]"
                                                    @click="handleMobileLinkClick"
                                                >
                                                    <div class="text-sm font-medium text-white">
                                                        {{ child.label }}
                                                    </div>

                                                    <div
                                                        v-if="child.description"
                                                        class="mt-1 text-xs leading-5 text-white/55"
                                                    >
                                                        {{ child.description }}
                                                    </div>
                                                </Link>
                                            </div>
                                        </transition>
                                    </div>

                                    <Link
                                        v-else
                                        :href="route(item.route_name, item.route_params || {})"
                                        class="rounded-2xl border border-transparent bg-white/[0.03] px-4 py-4 text-[16px] font-medium text-white/90 transition hover:border-white/8 hover:bg-white/[0.05]"
                                        @click="handleMobileLinkClick"
                                    >
                                        {{ item.label }}
                                    </Link>
                                </template>

                                <button
                                    type="button"
                                    @click.prevent="callbackStore.showBaseCallbackPopup({ title: 'Обратный звонок' }); closeMobileMenu()"
                                    class="mt-2 rounded-2xl border border-baseColor/25 bg-baseColor/10 px-4 py-4 text-left text-[16px] font-medium text-baseColor transition hover:bg-baseColor hover:text-black"
                                >
                                    Позвоните мне
                                </button>
                            </nav>

                            <div class="mt-6 border-t border-white/10 pt-4">
                                <BasePhoneComponent
                                    class="text-[15px] text-white"
                                    :highlightPrefix="true"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import {Link} from '@inertiajs/vue3'
import IconLogo from '@js/components/Icons/IconLogo.vue'
import IconChevronDown from '@js/components/Icons/IconChevronDown.vue'
import {useCallbackStore} from '@js/store/CallbackStore.js'
import BasePhoneComponent from '@js/components/BasePhoneComponent.vue'
import IconPhone from '@js/components/Icons/IconPhone.vue'
import TopBarDropdown from '@js/components/TopBarDropdown.vue'
import {useThemeStore} from "@js/store/ThemeStore.js"

export default {
    name: 'TopBar',
    components: {
        Link,
        IconPhone,
        BasePhoneComponent,
        IconChevronDown,
        IconLogo,
        TopBarDropdown,
    },
    props: {
        overlay: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            mobileMenuOpen: false,
            openedMobileDropdown: null,
            menuItems: [
                {
                    label: 'Услуги',
                    route_name: 'services.index',
                    children: [
                        {
                            label: 'Внедрение Битрикс24',
                            route_name: 'services.bitrix',
                            description: 'CRM, автоматизация, воронки и бизнес-процессы',
                        },
                        {
                            label: 'Продвижение Авито',
                            route_name: 'services.avito',
                            description: 'Упаковка, ведение и усиление продаж через Авито',
                        },
                        {
                            label: 'Разработка веб приложений',
                            route_name: 'services.development',
                            description: 'Корпоративные сайты, лендинги и веб-сервисы',
                        },
                    ],
                },
                {
                    label: 'Кейсы',
                    children: [
                        {
                            label: 'Веб-приложения',
                            route_name: 'cases.webapps',
                            description: 'Корпоративные сайты, магазины, CRM и B2B-системы.',
                        },
                    ],
                },
                {
                    label: 'О компании',
                    route_name: 'aboutCompany',
                },
            ],
        }
    },
    computed: {
        mobileMenuItems() {
            return [...this.menuItems]
        },
    },
    methods: {
        toggleMobileDropdown(label) {
            this.openedMobileDropdown =
                this.openedMobileDropdown === label ? null : label
        },
        handleMobileLinkClick() {
            this.mobileMenuOpen = false
            this.openedMobileDropdown = null
            this.themeStore.setBodyOverflow(false)
        },
        closeMobileMenu() {
            this.mobileMenuOpen = false
            this.themeStore.setBodyOverflow(false)
        },
        openMobileMenu() {
            this.mobileMenuOpen = true
            this.themeStore.setBodyOverflow(true)
        }
    },
    setup() {
        const callbackStore = useCallbackStore()
        const themeStore = useThemeStore()

        return {callbackStore, themeStore}
    },
}
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.collapse-enter-active,
.collapse-leave-active {
    transition: all 0.22s ease;
    overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
    opacity: 0;
    max-height: 0;
    transform: translateY(-4px);
}

.collapse-enter-to,
.collapse-leave-from {
    opacity: 1;
    max-height: 500px;
    transform: translateY(0);
}
</style>
